import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import {
  Container,
  TextField,
  Box,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  Typography,
  CssBaseline,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ImgEtiqa from "../../assets/etiqa.jpg";
import { ELanguage } from "../../enums/ELanguage";
import TabMakeAppointment from "../tabs/TabMakeAppointment";
import TabViewAppointment from "../tabs/TabViewAppointment";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import LatoBold from "../../fonts/Lato-Bold.ttf";
import LatoRegular from "../../fonts/Lato-Regular.ttf";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CApiRestful } from "../../api/CApiRestful";
import * as Constants from "../../constants";
import moment from "moment";

const TAG = "PgRoot";

interface IProps {}

const useStyles = makeStyles({
  root: {},
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3, pb: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const theme = createTheme({
  typography: {
    fontFamily: "Lato",
  },
  palette: {
    primary: {
      main: "#FECC00",
    },
  },
  components: {
    MuiFormLabel: {
      styleOverrides: {
        asterisk: { color: "red" },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'lato';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('lato'), local('lato'), url(${LatoRegular}) format('truetype');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
      `,
    },
  },
});

const PgRoot = (props: IProps) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState<ELanguage>(
    ELanguage.ENGLISH
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState(0);
  const [mobileNumber, setMobileNumber] = useState("");
  const [signature, setSignature] = useState("");
  const [jsonWsGetServiceListResp, setJsonWsGetServiceListResp] =
    useState<any>(undefined);
  const [jsonWsGetBranchListResp, setJsonWsGetBranchListResp] =
    useState<any>(undefined);
  const [isShowSessionTimeoutDlg, setIsShowSessionTimeoutDlg] = useState(false);
  const [counterSec, setCounterSec] = useState(
    Constants.SESSION_EXPIRY_POPUP_SEC
  );
  const [intervalId, setIntervalId] = useState<any>(undefined);

  useEffect(() => {
    const s = searchParams.get("s");
    const makeAppt = searchParams.get("make-appt");
    const mobile = searchParams.get("mobile-no");
    if (mobile && mobile.length > 0) {
      setMobileNumber(mobile.replace("+", "").trim());

      if (s && s.length > 0) {
        setSignature(s);
      }

      if (makeAppt && makeAppt.length > 0) {
        if (makeAppt == "1") {
          setSelectedTab(0);
        } else {
          setSelectedTab(1);
        }
      } else {
        setSelectedTab(1);
      }
      
      setSessionTimeout();
    } else {
      setMobileNumber("");
      setSelectedTab(0);
    }

    fetchServiceList().then(() => {
      fetchBranchList();
    });
  }, []);

  const setSessionTimeout = () => {
    try {
      setTimeout(() => {
        setIsShowSessionTimeoutDlg(true);
        const startLocalTime: any = new Date();

        const _intervalId = setInterval(() => {
          const dtNow: any = new Date();
          const diffLocalTime = dtNow - startLocalTime;
          const dtDiff =
            Constants.SESSION_EXPIRY_POPUP_SEC * 1000 - diffLocalTime;
          if (dtDiff > 0) {
            setCounterSec(moment.duration(dtDiff).seconds());
          } else {
            clearInterval(intervalId);
            navigate(-1);
          }
        }, 1000);
        setIntervalId(_intervalId);
      }, Constants.SESSION_EXPIRY_MIN * 60 * 1000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const fetchServiceList = async () => {
    try {
      setIsLoading(true);
      const jsonWsGetServiceListResp = await CApiRestful.getServiceList();
      setJsonWsGetServiceListResp(jsonWsGetServiceListResp);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBranchList = async () => {
    try {
      setIsLoading(true);
      const jsonWsGetBranchListResp = await CApiRestful.getBranchList();
      setJsonWsGetBranchListResp(jsonWsGetBranchListResp);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box p={2}>
        <Grid container justifyContent="flex-end">
          <TextField
            select
            sx={{ width: 160 }}
            value={selectedLanguage}
            size="small"
            onChange={(e) => {
              setSelectedLanguage(e.target.value as ELanguage);
              i18n.changeLanguage(e.target.value);
            }}
          >
            <MenuItem key={ELanguage.ENGLISH} value={ELanguage.ENGLISH}>
              {"English"}
            </MenuItem>
            <MenuItem key={ELanguage.MALAY} value={ELanguage.MALAY}>
              {"Malay"}
            </MenuItem>
          </TextField>
        </Grid>
      </Box>

      <Container>
        <Box pt={2} pb={2}>
          <Box textAlign="center" pb={2}>
            <img src={ImgEtiqa} height={100} />
          </Box>

          {mobileNumber.length > 0 ? (
            <Box>
              <Container maxWidth="sm" disableGutters>
                <Tabs
                  value={selectedTab}
                  onChange={(e, value) => {
                    setSelectedTab(value);
                  }}
                  style={{
                    border: "1px solid #BBBBBB",
                    borderRadius: "6px",
                  }}
                  TabIndicatorProps={{
                    sx: {
                      display: "none",
                    },
                  }}
                  variant="fullWidth"
                  aria-label="full width tabs"
                >
                  <Tab
                    style={{
                      textTransform: "none",
                      fontSize: "18px",
                      color: selectedTab == 0 ? "#FFFFFF" : "#000000",
                      backgroundColor:
                        selectedTab == 0 ? "#333333" : "transparent",
                    }}
                    label={t("root.makeAppointment")}
                  />
                  <Tab
                    style={{
                      textTransform: "none",
                      fontSize: "18px",
                      color: selectedTab == 1 ? "#FFFFFF" : "#000000",
                      backgroundColor:
                        selectedTab == 1 ? "#333333" : "transparent",
                    }}
                    label={t("root.viewAppointment")}
                  />
                </Tabs>
              </Container>
              <TabPanel value={selectedTab} index={0}>
                <Container maxWidth="sm" disableGutters>
                  <TabMakeAppointment
                    selectedLanguage={selectedLanguage}
                    mobileNumber={mobileNumber}
                    jsonWsGetBranchListResp={jsonWsGetBranchListResp}
                    jsonWsGetServiceListResp={jsonWsGetServiceListResp}
                    onAppointmentMade={() => {
                      setSelectedTab(1);
                    }}
                    signature={signature}
                  />
                </Container>
              </TabPanel>

              <TabPanel value={selectedTab} index={1}>
                <TabViewAppointment
                  selectedLanguage={selectedLanguage}
                  mobileNumber={mobileNumber}
                  jsonWsGetBranchListResp={jsonWsGetBranchListResp}
                  jsonWsGetServiceListResp={jsonWsGetServiceListResp}
                  signature={signature}
                />
              </TabPanel>
            </Box>
          ) : (
            <Container maxWidth="sm" disableGutters>
              <TabMakeAppointment
                selectedLanguage={selectedLanguage}
                mobileNumber={mobileNumber}
                jsonWsGetBranchListResp={jsonWsGetBranchListResp}
                jsonWsGetServiceListResp={jsonWsGetServiceListResp}
              />
            </Container>
          )}
        </Box>

        <Dialog
          open={isShowSessionTimeoutDlg}
          sx={{zIndex: 2000}}
          PaperProps={{
            style: { borderRadius: "12px", width: "400px" },
          }}
          keepMounted
        >
          <DialogTitle>
            <Box
              pl={3}
              pr={3}
              pt={3}
              fontSize={"24px"}
              fontWeight="bold"
              color={"#333333"}
            >
              {t("root.sessionExpiredTitle")}
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText pl={3} pr={3}>
              <Box color={"#000000"}>
                {t("root.sessionExpiredDesc1a") +
                  counterSec +
                  t("root.sessionExpiredDesc1b")}
              </Box>
              <Box pt={2} color={"#000000"}>
                {t("root.sessionExpiredDesc2")}
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Grid
              container
              pb={2}
              pl={2}
              pr={2}
              alignItems="center"
              justifyContent={"center"}
            >
              <Button
                  sx={{
                    width: "130px",
                    fontWeight: "bold",
                    fontSize: "16px",
                    height: "40px",
                    borderRadius: "4px",
                  color: "#000000",
                  }}
                  disableElevation
                  onClick={() => {
                    clearInterval(intervalId);
                    navigate(-1);
                  }}
                >
                  {t("root.logout")}
                </Button>
              <Button
                  sx={{
                    width: "130px",
                    fontWeight: "bold",
                    fontSize: "16px",
                    height: "40px",
                    borderRadius: "4px",
                    margin: "8px",
                  }}
                  disableElevation
                  variant="contained"
                  onClick={() => {
                    setIsShowSessionTimeoutDlg(false);
                    clearInterval(intervalId);
                    setSessionTimeout();
                  }}
                >
                  {t("root.continue")}
                </Button>
            </Grid>
          </DialogActions>
        </Dialog>

        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    </ThemeProvider>
  );
};

export default PgRoot;
